import { Card, Col, Row, Form, Switch, Button, Tooltip } from "antd";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  DateField,
  InputField,
  InputPhone,
  SelectField,
} from "../../../atoms/FormFields";
import { InfoIconOutlined } from "../../../atoms/CustomIcons";
import { createCompany } from "../ducks/services";
import { AxiosError, AxiosResponse } from "axios";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import { useNavigate } from "react-router-dom";
import FullPageLayout from "../../../templates/FullPageLayout";
import { uploadUserImage } from "../../MyProfile/ducks/services";
import { RcFile } from "antd/es/upload";
import { AutoCompleteField } from "../../../atoms/FormFields/AutoComplete";
import { AddressComponent, Place } from "../ducks/types";
import { timezoneList } from "../../../config/utils";
import { getClientManagerFormList } from "../ducks/actions";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";

interface FormValues {
  name: string;
  address: string;
  timezone: string;
  company_website: string;
  company_size: string;
  client_manager: string;
  first_name: string;
  last_name: string;
  first_language: string;
  email: string;
  phone: string;
  position_id: string;
  joined_date: string;
  birthday: string;
}

const firstLanguageOptions = [
  { label: "English", value: "english" },
  { label: "Spanish", value: "spanish" },
];

const companySizeDropdown = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "enterprise", label: "Enterprise" },
];

export default function CreateCompany() {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [isAccountOwnerVisible, setIsAccountOwnerVisible] = useState(false);
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const clientManagerList = useAppSelector(
    (state) => state?.company?.companyClientManager
  );

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    const payload = {
      name: val?.name?.trim(),
      address: val?.address,
      city: city,
      state: state,
      timezone: val?.timezone,
      company_website: val?.company_website,
      company_size: val?.company_size,
      client_manager: val?.client_manager,
      isAccountOwner: isAccountOwnerVisible,
      user: isAccountOwnerVisible
        ? {
            first_name: val?.first_name,
            last_name: val?.last_name,
            first_language: val?.first_language,
            position_id: val?.position_id,
            type: "client",
            email: val?.email,
            phone: val?.phone,
            joined_date: val?.joined_date ? val?.joined_date : undefined,
            birthday: val?.birthday ? val?.birthday : undefined,
          }
        : undefined,
    };

    createCompany(payload)
      .then((res: AxiosResponse) => {
        if (checklistFile && res && res?.data?.id) {
          uploadUserImage({
            owner_id: res?.data?.id,
            image: checklistFile as File | RcFile,
            image_type: "company_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("Company created.");
                navigate("/companies");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("Company created.");
          navigate("/companies");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    dispatch(getClientManagerFormList());
  }, [dispatch]);

  return (
    <FullPageLayout loading={loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col style={{ width: "672px", paddingBottom: 90 }}>
          <div className="title">
            <h2>Create company</h2>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <UploadPhoto
                  size={86}
                  darkBg
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  onImageDelete={() => null}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  uploadBtnText="Upload logo"
                  infoTextUploader="Customize the profile by adding a company logo."
                />
              </Col>

              <Col span={24} className="createForm">
                <h4> Company details </h4>
                <InputField
                  fieldName="name"
                  label="Company Name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter company name",
                    autoComplete: "company_name",
                  }}
                  isRequired
                  rules={{
                    required: "Company name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />

                <AutoCompleteField
                  fieldName="address"
                  label="Headquarters address"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Search address",
                  }}
                  onPlaceChange={(place: Place) => setGoogleAddress(place)}
                  isRequired
                  rules={{
                    required: "Headquarters address is required.",
                  }}
                  validate={errors.address && "error"}
                  validMessage={errors.address && errors.address.message}
                />

                <SelectField
                  fieldName="timezone"
                  label="Headquarters time zone"
                  control={control}
                  selectOption={timezoneList}
                  iProps={{
                    placeholder: "Select time zone",
                  }}
                  isRequired
                  rules={{
                    required: "Timezone is required.",
                  }}
                  validate={errors.timezone && "error"}
                  validMessage={errors.timezone && errors.timezone.message}
                  tooltipTitle="All users will see the same time, based on this time zone, regardless of their actual location. For example, if an executive travels, they’ll still see the work schedule in this “home” time zone."
                />

                <InputField
                  fieldName="company_website"
                  label="Company website"
                  control={control}
                  iProps={{
                    placeholder: "Enter Website",
                    autoComplete: "company_website",
                  }}
                  tooltipTitle="The website domain will be suggested as the email domain for company employee accounts."
                  // rules={{
                  //   pattern: {
                  //     value: /^(www):\/\/[^ "]+$/,
                  //     message: "Please enter a valid URL.",
                  //   },
                  // }}
                  // validate={errors.company_website && "error"}
                  // validMessage={
                  //   errors.company_website && errors.company_website.message
                  // }
                />

                <SelectField
                  fieldName="company_size"
                  label="Company size"
                  control={control}
                  selectOption={companySizeDropdown}
                  iProps={{
                    placeholder: "Select company size",
                  }}
                  className="bottom_0"
                />
              </Col>
            </Row>
          </Card>

          <Card>
            <Row gutter={20}>
              <Col span={24} className="createForm">
                <div className="toggle_area">
                  <div className="title_area">
                    <Switch
                      className="account_owner_btn"
                      checked={isAccountOwnerVisible}
                      onChange={(checked) => setIsAccountOwnerVisible(checked)}
                    />
                    <h4> Add company account owner </h4>
                  </div>
                  <Tooltip
                    arrow={false}
                    placement="topLeft"
                    title="Creates a position (like CEO, Owner) and a user who will be a master user of company’s account. Only these users can submit requests for company account deletion."
                  >
                    <Button type="text" className="info_btn outlined-btn">
                      <InfoIconOutlined />
                    </Button>
                  </Tooltip>
                </div>

                {isAccountOwnerVisible && (
                  <Row gutter={20} className="account_owner_form">
                    <Col span={24}>
                      <InputField
                        fieldName="position_id"
                        label="Position name"
                        control={control}
                        defaultValue=""
                        iProps={{
                          placeholder: "Enter position name",
                        }}
                        isRequired
                        rules={{
                          required: "Position name is required.",
                        }}
                        validate={errors.position_id && "error"}
                        validMessage={
                          errors.position_id && errors.position_id.message
                        }
                        hintMessage="This position will be created in the company org
                        structure."
                      />
                    </Col>
                    <Col span={12}>
                      <InputField
                        fieldName="first_name"
                        label="First name"
                        control={control}
                        defaultValue=""
                        iProps={{
                          placeholder: "Enter first name",
                          autoComplete: "first_name",
                          maxLength: 100,
                        }}
                        isRequired
                        rules={{
                          required: "First name is required.",
                        }}
                        validate={errors.first_name && "error"}
                        validMessage={
                          errors.first_name && errors.first_name.message
                        }
                      />
                    </Col>
                    <Col span={12}>
                      <InputField
                        fieldName="last_name"
                        label="Last name"
                        control={control}
                        defaultValue=""
                        iProps={{
                          placeholder: "Enter last name",
                          autoComplete: "last_name",
                          maxLength: 100,
                        }}
                        isRequired
                        rules={{
                          required: "Last name is required.",
                        }}
                        validate={errors.last_name && "error"}
                        validMessage={
                          errors.last_name && errors.last_name.message
                        }
                      />
                    </Col>
                    <Col span={24}>
                      <SelectField
                        fieldName="first_language"
                        label="First Language"
                        control={control}
                        selectOption={firstLanguageOptions}
                        iProps={{ placeholder: "Select first language" }}
                        defaultValue="english"
                        isRequired
                        rules={{
                          required: "Last name is required.",
                        }}
                        validate={errors.first_language && "error"}
                        validMessage={
                          errors.first_language && errors.first_language.message
                        }
                      />
                    </Col>

                    <Col span={12}>
                      <InputField
                        fieldName="email"
                        label="Account email"
                        control={control}
                        defaultValue=""
                        iProps={{
                          placeholder: "Enter email",
                          autoComplete: "email",
                          maxLength: 100,
                        }}
                        isRequired
                        rules={{
                          required: "Email address is required.",
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Please enter a valid email address.",
                          },
                        }}
                        validate={errors.email && "error"}
                        validMessage={errors.email && errors.email.message}
                      />
                    </Col>
                    <Col span={12}>
                      <InputPhone
                        fieldName="phone"
                        label="Phone"
                        control={control}
                      />
                    </Col>
                    <Col span={12} className="mb-0">
                      <DateField
                        defaultValue=""
                        fieldName="joined_date"
                        label="Joined company"
                        control={control}
                        iProps={{
                          placeholder: "MM/DD/YYYY",
                        }}
                      />
                    </Col>
                    <Col span={12} className="mb-0">
                      <DateField
                        defaultValue=""
                        fieldName="birthday"
                        label="Birthdate"
                        control={control}
                        iProps={{
                          placeholder: "MM/DD/YYYY",
                          format: "MMM D",
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Card>

          <Card>
            <Row gutter={20}>
              <Col span={24} className="createForm mb-0">
                <h4> Client manager </h4>
                <SelectField
                  fieldName="client_manager"
                  label="Client manager"
                  control={control}
                  selectOption={clientManagerList}
                  iProps={{ placeholder: "Select client manager" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate("/companies")}
              >
                Cancel
              </Button>
              <Button htmlType="submit" type="primary" className="mint-btn">
                Create company
              </Button>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
